import React, { useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, InputAdornment } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { Close } from '@material-ui/icons';
import { isNil } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../../../../../../ui-kit';
import { DeliveryListFormValues } from '../../../types';
import { usePriceService } from '../../../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../../../utils/hooks/auth';
import { localizeEnum, useDebounce } from '../../../../../../../utils';
import { UnitType } from '../../../../../../../generated/graphql';
import InputFieldWithCallback, { NumberInputFieldChangeEvent } from './InputFieldWithCallback';
import CounterField from './CounterField';
import { DeliveryItemContainer } from './style';

interface DeliveryItemProps {
    stockCard: DeliveryListFormValues['deliveryListItems'][0];
    index: number;
    handleRemove(index: number): void;
}

const DeliveryItem = ({ stockCard, index, handleRemove }: DeliveryItemProps) => {
    const { user: { defaultCurrency } } = useUser();
    const { t } = useTranslation();
    const { priceService } = usePriceService();
    const formikProps = useFormikContext<DeliveryListFormValues>();
    const pricePerUnitName = `deliveryListItems.${index}.priceWithoutVat`;
    const totalCostName = `deliveryListItems.${index}.totalCost`;
    const quantityName = `deliveryListItems.${index}.quantity`;
    const { setFieldValue, values: { deliveryListItems } } = formikProps;
    const { quantity, priceWithoutVat, totalCost } = deliveryListItems[index];
    const quantityRef = useRef(quantity);
    const priceWithoutVatInitialValue = useRef(priceWithoutVat);
    const totalCostInitialValue = useRef(totalCost);

    const debouncedQuantity = useDebounce(quantity);

    const updateTotalCostCallback = useCallback(
        (changedQuantity: number) => {
            const parsedPriceWithoutVat = priceService.toInteger(priceWithoutVat, defaultCurrency.currencyCode);
            const totalCostAsInt = priceService.multiply(parsedPriceWithoutVat, changedQuantity, defaultCurrency.currencyCode);
            const totalCostAsFloat = priceService.toUnit(totalCostAsInt);
            setFieldValue(totalCostName, totalCostAsFloat);
        },
        [defaultCurrency.currencyCode, priceService, priceWithoutVat, setFieldValue, totalCostName],
    );
    useEffect(() => {
        const previousQuantity = quantityRef.current;
        if (previousQuantity !== debouncedQuantity) {
            updateTotalCostCallback(debouncedQuantity);
            quantityRef.current = debouncedQuantity;
        }
    }, [debouncedQuantity, updateTotalCostCallback]);

    const priceWithoutVatCallback = ({ target }: NumberInputFieldChangeEvent) => {
        const { value } = target;

        const { amount: parsedPriceWithoutVat, precision } = priceService.parseIntegerFromFloat(value);
        const updatedTotalCost = priceService.multiply(parsedPriceWithoutVat, quantity, defaultCurrency.currencyCode);
        setFieldValue(totalCostName, priceService.integerToFloatWithPrecision(updatedTotalCost, precision));
    };
    const totalCostCallback = (event: NumberInputFieldChangeEvent) => {
        const { target: { value } } = event;
        if (isNil(value)) {
            return;
        }
        const parsedValue = priceService.toInteger(value);
        const updatedPriceWithoutVat = priceService.divide(parsedValue, quantity, defaultCurrency.currencyCode);
        const updatedPriceWithoutVatFloat = priceService.toUnit(updatedPriceWithoutVat);
        setFieldValue(pricePerUnitName, updatedPriceWithoutVatFloat);
    };

    const handleRemoveClick = () => handleRemove(index);

    return (
        <DeliveryItemContainer>
            <Box />
            <Box justifySelf='end'>
                <IconButton size='small' color='secondary' onClick={handleRemoveClick}>
                    <Close color='inherit' />
                </IconButton>
            </Box>
            <Typography variant='subtitle1'>
                {stockCard.name}
            </Typography>
            <CounterField
                maxDecimals={3}
                name={quantityName}
                minLimit={0}
                initialValue={quantity}
                endAdornment={(
                    <InputAdornment position="start">
                        {localizeEnum(t)(stockCard.storageUnit, { UnitType })}
                    </InputAdornment>
                )}
                formikProps={formikProps}
            />
            <InputFieldWithCallback
                onChangeCallback={priceWithoutVatCallback}
                label={t('Price without VAT')}
                name={pricePerUnitName}
                formikProps={formikProps}
                type="number"
                initialValue={priceWithoutVatInitialValue.current}
            />
            <InputFieldWithCallback
                onChangeCallback={totalCostCallback}
                label={t('Total cost without VAT')}
                name={totalCostName}
                formikProps={formikProps}
                type="number"
                initialValue={totalCostInitialValue.current}

            />
        </DeliveryItemContainer>
    );
};

export default DeliveryItem;
